import React, { useState, useEffect, useContext, useCallback } from 'react';
import { eventsApi } from '../../api';
import { UserContext } from '../../context/UserContext';
import { isAdmin } from '../../admin/utils/roles';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbDatePicker from '../controls/HrnbDatePicker';
import HrnbFormControl from '../controls/HrnbFormControl';

const RESPONSE_OPTIONS = [
    { value: 'GOING', label: 'Пойду', variant: 'primary' },
    { value: 'MAYBE', label: 'Возможно', variant: 'secondary' },
    { value: 'DECLINED', label: 'Не пойду', variant: 'danger' },
];

const REMINDER_CHANNEL_OPTIONS = [
    { value: 'IN_APP', label: 'В приложении' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'PUSH', label: 'Push' },
];

export default function EventDetailModal({ event, open, onClose, onUpdate }) {
    const { currentUser } = useContext(UserContext);
    const [participants, setParticipants] = useState([]);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [responding, setResponding] = useState(false);
    const [error, setError] = useState(null);
    const [userResponse, setUserResponse] = useState(null);

    // Reminder state
    const [reminders, setReminders] = useState([]);
    const [loadingReminders, setLoadingReminders] = useState(false);
    const [showAddReminder, setShowAddReminder] = useState(false);
    const [reminderDate, setReminderDate] = useState('');
    const [reminderTime, setReminderTime] = useState('');
    const [reminderChannel, setReminderChannel] = useState('IN_APP');
    const [addingReminder, setAddingReminder] = useState(false);

    // Delete state
    const [deleting, setDeleting] = useState(false);

    const isOrganizer = currentUser && event?.organizerId === currentUser.id;
    const canManage = isOrganizer || isAdmin(currentUser);

    const fetchParticipants = useCallback(async () => {
        setLoadingParticipants(true);
        try {
            const data = await eventsApi.getParticipants(event.id);
            const list = Array.isArray(data) ? data : (data?.content || []);
            setParticipants(list);
            if (currentUser) {
                const me = list.find((p) => p.userId === currentUser.id || p.id === currentUser.id);
                if (me) setUserResponse(me.status);
            }
        } catch (err) {
            console.error('[EventDetailModal] Failed to fetch participants:', err.message);
        } finally {
            setLoadingParticipants(false);
        }
    }, [event?.id, currentUser]);

    const fetchReminders = useCallback(async () => {
        if (!canManage) return;
        setLoadingReminders(true);
        try {
            const data = await eventsApi.getReminders(event.id);
            const list = Array.isArray(data) ? data : (data?.content || []);
            setReminders(list);
        } catch (err) {
            console.error('[EventDetailModal] Failed to fetch reminders:', err.message);
        } finally {
            setLoadingReminders(false);
        }
    }, [event?.id, canManage]);

    useEffect(() => {
        if (!open || !event?.id) return;
        setError(null);
        setUserResponse(null);
        setShowAddReminder(false);
        fetchParticipants();
        fetchReminders();
    }, [open, event?.id, fetchParticipants, fetchReminders]);

    const handleRespond = async (status) => {
        if (!event?.id) return;
        setResponding(true);
        setError(null);
        try {
            await eventsApi.respond(event.id, status);
            setUserResponse(status);
            await fetchParticipants();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('[EventDetailModal] Failed to respond:', err.message);
            setError('Не удалось отправить ответ');
        } finally {
            setResponding(false);
        }
    };

    const handleAddReminder = async (e) => {
        e.preventDefault();
        if (!reminderDate || !reminderTime) return;
        setAddingReminder(true);
        try {
            await eventsApi.createReminder(event.id, {
                reminderTime: `${reminderDate} ${reminderTime}`,
                channel: reminderChannel,
            });
            setReminderDate('');
            setReminderTime('');
            setReminderChannel('IN_APP');
            setShowAddReminder(false);
            await fetchReminders();
        } catch (err) {
            console.error('[EventDetailModal] Failed to add reminder:', err.message);
            setError('Не удалось создать напоминание');
        } finally {
            setAddingReminder(false);
        }
    };

    const handleDeleteReminder = async (reminderId) => {
        try {
            await eventsApi.removeReminder(event.id, reminderId);
            await fetchReminders();
        } catch (err) {
            console.error('[EventDetailModal] Failed to delete reminder:', err.message);
            setError('Не удалось удалить напоминание');
        }
    };

    const handleDeleteEvent = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить это событие?')) return;
        setDeleting(true);
        try {
            await eventsApi.remove(event.id);
            if (onUpdate) onUpdate();
            onClose();
        } catch (err) {
            console.error('[EventDetailModal] Failed to delete event:', err.message);
            setError('Не удалось удалить событие');
        } finally {
            setDeleting(false);
        }
    };

    if (!event) return null;

    const start = parseDate(event.startTime);
    const end = parseDate(event.endTime);

    const goingCount = participants.filter((p) => p.status === 'GOING').length;
    const maybeCount = participants.filter((p) => p.status === 'MAYBE').length;

    return (
        <HrnbModal open={open} onClose={onClose} title={event.title}>
            <div className="event-detail">
                {error && <HrnbAlert type="danger" onClose={() => setError(null)}>{error}</HrnbAlert>}

                <div className="event-detail-meta">
                    <span className={`event-detail-type event-detail-type--${(event.eventType || 'CUSTOM').toLowerCase()}`}>
                        {getEventTypeLabel(event.eventType)}
                    </span>
                    {event.status && (
                        <span className={`event-detail-status event-detail-status--${event.status.toLowerCase()}`}>
                            {getStatusLabel(event.status)}
                        </span>
                    )}
                </div>

                {event.description && (
                    <p className="event-detail-desc">{event.description}</p>
                )}

                <div className="event-detail-dates">
                    <div className="event-detail-date-row">
                        <span className="event-detail-label">Начало:</span>
                        <span>{start ? formatDateTime(start) : '—'}</span>
                    </div>
                    {end && (
                        <div className="event-detail-date-row">
                            <span className="event-detail-label">Окончание:</span>
                            <span>{formatDateTime(end)}</span>
                        </div>
                    )}
                    {event.timezone && (
                        <div className="event-detail-date-row">
                            <span className="event-detail-label">Часовой пояс:</span>
                            <span>{event.timezone}</span>
                        </div>
                    )}
                    {event.location && (
                        <div className="event-detail-date-row">
                            <span className="event-detail-label">Место:</span>
                            <span>{event.location}</span>
                        </div>
                    )}
                    {event.url && (
                        <div className="event-detail-date-row">
                            <span className="event-detail-label">Ссылка:</span>
                            <a href={event.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2399E3' }}>
                                {event.url}
                            </a>
                        </div>
                    )}
                </div>

                {event.organizer && (
                    <div className="event-detail-organizer">
                        <span className="event-detail-label">Организатор:</span>
                        <span>{typeof event.organizer === 'string' ? event.organizer : event.organizer?.username || event.organizer?.name || '—'}</span>
                    </div>
                )}

                {event.relatedEntityType && event.relatedEntityId && (
                    <div className="event-detail-related">
                        <span className="event-detail-label">Связано с:</span>
                        <span>{getRelatedLabel(event.relatedEntityType)}</span>
                    </div>
                )}

                {/* Блок ответа на приглашение */}
                {currentUser && event.status !== 'CANCELLED' && (
                    <div className="event-detail-response">
                        <h5>Ваш ответ:</h5>
                        <div className="event-detail-response-buttons">
                            {RESPONSE_OPTIONS.map((opt) => (
                                <HrnbButton
                                    key={opt.value}
                                    variant={userResponse === opt.value ? opt.variant : 'outline'}
                                    onClick={() => handleRespond(opt.value)}
                                    disabled={responding}
                                    className={userResponse === opt.value ? 'event-detail-response--active' : ''}
                                >
                                    {opt.label}
                                </HrnbButton>
                            ))}
                        </div>
                    </div>
                )}

                {/* Участники */}
                <div className="event-detail-participants">
                    <h5>Участники</h5>
                    <div className="event-detail-participants-stats">
                        <span className="stat-going">{goingCount} пойдут</span>
                        <span className="stat-maybe">{maybeCount} возможно</span>
                        <span className="stat-total">{participants.length} всего</span>
                    </div>

                    {loadingParticipants ? (
                        <HrnbLoader size={24} text="Загрузка..." />
                    ) : participants.length === 0 ? (
                        <p className="event-detail-no-participants">Пока никто не ответил</p>
                    ) : (
                        <ul className="event-detail-participants-list">
                            {participants.map((p) => (
                                <li key={p.userId || p.id || Math.random()} className={`event-detail-participant event-detail-participant--${(p.status || '').toLowerCase()}`}>
                                    <span className="event-detail-participant-name">
                                        {p.username || p.name || p.user?.username || 'Участник'}
                                    </span>
                                    <span className="event-detail-participant-status">
                                        {getResponseLabel(p.status)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Напоминания (только для организатора/админа) */}
                {canManage && (
                    <div className="event-detail-reminders">
                        <h5>Напоминания</h5>

                        {loadingReminders ? (
                            <HrnbLoader size={24} text="Загрузка..." />
                        ) : reminders.length === 0 ? (
                            <p className="event-detail-no-reminders">Нет напоминаний</p>
                        ) : (
                            <ul className="event-detail-reminders-list">
                                {reminders.map((r) => (
                                    <li key={r.id} className="event-detail-reminder">
                                        <span className="event-detail-reminder-time">
                                            {formatDateTime(parseDate(r.reminderTime))}
                                        </span>
                                        <span className={`event-detail-reminder-channel event-detail-reminder-channel--${r.channel.toLowerCase()}`}>
                                            {r.channel === 'IN_APP' ? 'В приложении' : r.channel === 'EMAIL' ? 'Email' : 'Push'}
                                        </span>
                                        <button
                                            className="event-detail-reminder-delete"
                                            onClick={() => handleDeleteReminder(r.id)}
                                            title="Удалить"
                                        >
                                            ×
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {!showAddReminder ? (
                            <HrnbButton
                                variant="outline"
                                size="sm"
                                onClick={() => setShowAddReminder(true)}
                                style={{ marginTop: 12 }}
                            >
                                + Добавить напоминание
                            </HrnbButton>
                        ) : (
                            <form onSubmit={handleAddReminder} className="event-detail-reminder-form">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                                    <HrnbDatePicker
                                        label="Дата"
                                        value={reminderDate}
                                        onChange={setReminderDate}
                                        maxDate={event.startTime?.slice(0, 10)}
                                    />
                                    <HrnbFormControl
                                        label="Время"
                                        type="time"
                                        value={reminderTime}
                                        onChange={(e) => setReminderTime(e.target.value)}
                                    />
                                </div>
                                <HrnbSelect
                                    label="Канал"
                                    value={reminderChannel}
                                    onChange={setReminderChannel}
                                    options={REMINDER_CHANNEL_OPTIONS}
                                    style={{ marginTop: 8 }}
                                />
                                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                    <HrnbButton variant="outline" size="sm" onClick={() => setShowAddReminder(false)}>
                                        Отмена
                                    </HrnbButton>
                                    <HrnbButton type="submit" variant="primary" size="sm" disabled={addingReminder || !reminderDate || !reminderTime}>
                                        {addingReminder ? 'Добавление...' : 'Добавить'}
                                    </HrnbButton>
                                </div>
                            </form>
                        )}
                    </div>
                )}

                {/* Действия организатора/админа */}
                {canManage && (
                    <div className="event-detail-actions">
                        <HrnbButton
                            variant="danger"
                            onClick={handleDeleteEvent}
                            disabled={deleting}
                        >
                            {deleting ? 'Удаление...' : 'Удалить событие'}
                        </HrnbButton>
                    </div>
                )}
            </div>
        </HrnbModal>
    );
}

function parseDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
}

function formatDateTime(date) {
    if (!date) return '';
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getEventTypeLabel(type) {
    const labels = {
        BATTLE_START: 'Баттл',
        ROUND_START: 'Раунд',
        ROUND_END: 'Раунд',
        VOTING_START: 'Голосование',
        VOTING_END: 'Голосование',
        REGISTRATION_DEADLINE: 'Дедлайн регистрации',
        TRACK_UPLOAD_DEADLINE: 'Дедлайн загрузки',
        LIVE_STREAM: 'Стрим',
        MEETING: 'Встреча',
        CUSTOM: 'Другое',
    };
    return labels[type] || type || 'Другое';
}

function getStatusLabel(status) {
    const labels = {
        PUBLISHED: 'Опубликовано',
        DRAFT: 'Черновик',
        CANCELLED: 'Отменено',
    };
    return labels[status] || status;
}

function getResponseLabel(status) {
    const labels = {
        GOING: 'Пойдёт',
        MAYBE: 'Возможно',
        DECLINED: 'Не пойдёт',
    };
    return labels[status] || status;
}

function getRelatedLabel(type) {
    const labels = {
        battle: 'Баттл',
        round: 'Раунд',
        user: 'Пользователь',
    };
    return labels[type] || type;
}
